import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import {
  useGetRestrictedCustomersQuery,
  useRemoveRestrictionMutation,
} from 'api/recoveryAi'
import type { RestrictedCustomer } from 'api/recoveryAi.types'
import ConfirmModal from 'components/ConfirmModal'
import SearchInput from 'components/SearchInput'
import TablePaginationFooter, {
  DEFAULT_PAGE_SIZE,
} from 'components/TablePaginationFooter'
import type { PageSize } from 'components/TablePaginationFooter'
import { buildTranslate } from 'locales'
import escape from 'lodash/escape'
import React, { useEffect, useRef, useState } from 'react'
import { useStyletron } from 'styletron-react'
import { useDebouncedCallback } from 'use-debounce'
import { useAbility } from 'utilities/ability'
import RestrictedCustomersTable from './RestrictedCustomersTable'

const t = buildTranslate('recovery_ai.restricted_customers')

// Exported so the co-located test advances fake timers by the real interval
// rather than a duplicated literal (mirrors SKELETON_ROW_COUNT in the table).
export const SEARCH_DEBOUNCE_MS = 300

// Scene-level pane (data fetching lives here, not in the presentational table).
// Composes the list query + table + pagination + a debounced search + the
// Remove-restriction confirm flow. Admin-only Remove is gated on the
// MerchantSettings CASL ability (mirrors EscalationRulesPane / CaseDetail);
// non-admins are read-only — the search box is disabled and the row Remove
// action withheld.
const RestrictedCustomers = () => {
  const [css] = useStyletron()
  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE)
  const [pendingRow, setPendingRow] = useState<RestrictedCustomer | null>(null)

  const { data, isLoading, isError, refetch } = useGetRestrictedCustomersQuery({
    page: currentPage,
    per_page: pageSize,
    // Drives the live AI-207 (#642) server-side name filter (case-insensitive
    // first/last-name substring). `search || undefined` keeps a cleared box a
    // no-op: it drops the param so the endpoint returns the full unfiltered set.
    search: search || undefined,
  })

  const [
    removeRestriction,
    { isLoading: isRemoving, isError: isRemoveError, reset },
  ] = useRemoveRestrictionMutation()

  const rows = data?.data ?? []
  const totalPages = data?.meta.totalPages ?? 1
  const totalCount = data?.meta.totalCount ?? 0

  // WCAG 4.1.3 (Status Messages): search-as-you-type swaps the table body in
  // place, so a screen-reader user otherwise gets no feedback. Announce the
  // filtered count via the polite live region below — but only once a term is
  // active (not on initial load or pagination) and the query has settled.
  const hasActiveSearch = search.length > 0
  const searchResultsAnnouncement =
    hasActiveSearch && !isLoading && !isError
      ? t(
          totalCount === 1
            ? 'search_results_announcement_one'
            : 'search_results_announcement_other',
          { count: totalCount }
        )
      : ''

  const [debouncedSetSearch, cancelDebouncedSearch] = useDebouncedCallback(
    (value: string) => {
      setSearch(value)
      setCurrentPage(1)
    },
    SEARCH_DEBOUNCE_MS
  )

  // Drop any in-flight debounced update on unmount so we don't setState on an
  // unmounted tree.
  useEffect(() => cancelDebouncedSearch, [cancelDebouncedSearch])

  // A failed-remove banner is instance-scoped and would otherwise linger across
  // page/search changes, reading as an active error on an unrelated slice. Clear
  // it whenever the viewed slice changes (consistent with closeConfirm's reset()).
  // Skip the initial mount — there's no prior error to clear, and resetting then
  // would be a spurious call the close-flow test rightly pins down.
  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    reset()
  }, [currentPage, search, reset])

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSetSearch(value)
  }

  const closeConfirm = () => {
    // Clear any prior mutation error so a reopened / next-row confirm shows no
    // stale banner — mutation error state is instance-scoped and survives.
    reset()
    setPendingRow(null)
  }

  const handleConfirmRemove = () => {
    if (!pendingRow) return
    removeRestriction(pendingRow.id)
      .unwrap()
      .then(() => {
        setPendingRow(null)
        // Removing the last row on a non-first page would leave the operator on
        // a now-empty page with the pagination control hidden (rows.length > 0)
        // and no way out. Reset to page 1 — mirrors the search reset above.
        setCurrentPage(1)
      })
      .catch(() => {
        // Keep the modal open; the inline negative Notification (isRemoveError)
        // surfaces the failure — INCLUDING the admin-gate 403 (RBAC off → the
        // DELETE is admin-only, so a manage-only non-admin gets 403). Never
        // swallowed. The rejection is handled here at the call site, so no
        // per-endpoint onQueryStarted is needed (a single error mechanism).
      })
  }

  return (
    <div>
      <div className={`d-flex flex-column ${css({ marginBottom: '52px' })}`}>
        <Text.Header4 bold color="grey90">
          {t('title')}
        </Text.Header4>
        <Text.BodyText3 color="grey70" className="mt-xs">
          {t('description')}
        </Text.BodyText3>
      </div>

      {/* Live server-side name filter (AI-207 #642): case-insensitive first/last-name
          substring, server-side. The debounced `search` param resets to page 1 on
          change; a cleared box is a no-op (drops the param → full unfiltered list). */}
      <div className={css({ marginBottom: '24px', width: '30%' })}>
        <SearchInput
          value={searchInput}
          onChange={handleSearchChange}
          placeholder={t('search_placeholder')}
          ariaLabel={t('search_placeholder')}
          disabled={!canManage}
        />
      </div>

      {/* Polite, visually-hidden live region (WCAG 4.1.3): announces the filtered
          result count to screen readers when a search is active, since the table
          body updates silently as the operator types. Always rendered (empty
          until a search is active) so assistive tech registers the change. */}
      <div role="status" aria-live="polite" className="sr-only">
        {searchResultsAnnouncement}
      </div>

      {isRemoveError && (
        <div className={css({ marginBottom: '24px' })}>
          <Notification kind="negative" size="full" closeable onClose={reset}>
            {t('remove.error')}
          </Notification>
        </div>
      )}

      <RestrictedCustomersTable
        rows={rows}
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        canManage={canManage}
        onRemove={setPendingRow}
      />

      <TablePaginationFooter
        page={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        onPageSizeChange={size => {
          setPageSize(size)
          setCurrentPage(1)
        }}
      />

      <ConfirmModal
        isOpen={!!pendingRow}
        title={t('remove.title')}
        // ConfirmModal renders `description` via dangerouslySetInnerHTML, so the
        // interpolated customer name (server-controlled wire data) is HTML-escaped
        // to prevent injection into the operator's authenticated admin session.
        description={t('remove.body', {
          name: escape(pendingRow?.customerName ?? ''),
        })}
        confirmText={t('remove.confirm')}
        isLoading={isRemoving}
        onClose={closeConfirm}
        onConfirm={handleConfirmRemove}
      />
    </div>
  )
}

export default RestrictedCustomers
